import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { calculateAge } from '../Auth/AuthPage';
import { updateProfile } from './myPageApi';

const genderLabels = { male: '남', female: '여' };

// 서버 user를 화면·폼에서 쓰는 형태로 정리한다. 생년월일은 YYYY-MM-DD로 자른다.
function toProfile(user = {}) {
    return {
        name: user.name || '',
        email: user.email || '',
        birthDate: (user.birthDate || '').slice(0, 10),
        gender: user.gender || '',
        affiliation: user.affiliation || '',
    };
}

// 회원가입과 같은 기준으로 편집 값을 검증하고 필드별 오류 문구를 돌려준다.
function validateProfile(values) {
    const today = new Date().toISOString().slice(0, 10);
    const birth = new Date(`${values.birthDate}T00:00:00Z`);
    const birthValid = /^\d{4}-\d{2}-\d{2}$/.test(values.birthDate)
        && !Number.isNaN(birth.getTime()) && birth.toISOString().slice(0, 10) === values.birthDate
        && values.birthDate >= '1900-01-01' && values.birthDate <= today;
    const name = values.name.trim();
    return {
        name: name.length < 2 || name.length > 50 ? '이름을 2~50자로 입력해주세요.' : '',
        birthDate: birthValid ? '' : '올바른 생년월일을 입력해주세요.',
        gender: genderLabels[values.gender] ? '' : '성별을 선택해주세요.',
        affiliation: values.affiliation.trim().length > 150 ? '소속은 150자 이내로 입력해주세요.' : '',
    };
}

// 로그인 방식을 화면 문구로 만든다. 비밀번호 계정과 연결된 소셜 계정을 함께 표시한다.
function loginMethods(hasPassword, socialAccounts) {
    const socialLabels = { naver: '네이버' };
    const methods = socialAccounts.map(account => {
        const provider = typeof account === 'string' ? account : account?.provider;
        return socialLabels[provider] || provider;
    }).filter(Boolean);
    return [...(hasPassword ? ['이메일'] : []), ...methods].join(' · ');
}

// 비어 있는 값은 '-'로 표시한다.
function display(value) {
    return value || '-';
}

// 기본 정보 조회와 편집 화면. 저장 시 PATCH /api/mypage를 호출한다.
export default function ProfileSection() {
    const { myPage, updateUser } = useOutletContext();
    const profile = toProfile(myPage.user);
    const [draft, setDraft] = useState(profile);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState({});
    const [error, setError] = useState('');
    const errors = validateProfile(draft);
    const formValid = Object.values(errors).every(message => !message);

    // 편집 모드로 전환하고 현재 값을 입력란에 채운다.
    function startEdit() {
        setDraft(profile);
        setTouched({});
        setError('');
        setEditing(true);
    }

    // 입력란 변경 값을 편집 중인 초안에 반영한다.
    function change(event) {
        const { name, value } = event.target;
        setDraft(previous => ({ ...previous, [name]: value }));
    }

    // 입력란을 벗어나면 해당 필드의 오류를 보이게 한다.
    function touch(event) {
        const { name } = event.target;
        setTouched(previous => ({ ...previous, [name]: true }));
    }

    // 필드에 오류가 있고 사용자가 건드렸을 때만 오류 문구를 보여준다.
    function fieldError(name) {
        return touched[name] && errors[name] ? <small className="my-page-field-error" role="alert">{errors[name]}</small> : null;
    }

    // 검증을 통과한 초안을 서버에 저장하고 결과 user를 반영한다.
    async function save(event) {
        event.preventDefault();
        setTouched({ name: true, birthDate: true, gender: true, affiliation: true });
        if (!formValid || saving) return;
        setSaving(true);
        setError('');
        try {
            const user = await updateProfile({ name: draft.name.trim(), birthDate: draft.birthDate, gender: draft.gender, affiliation: draft.affiliation.trim() });
            updateUser(user);
            setEditing(false);
            window.alert('기본 정보가 수정되었습니다.');
        } catch (saveError) {
            setError(saveError.message);
        } finally {
            setSaving(false);
        }
    }

    const age = calculateAge(profile.birthDate);
    const today = new Date().toISOString().slice(0, 10);

    return <>
        <div className="my-page-section-heading"><div><h2>기본 정보</h2><p>가입할 때 입력한 정보를 확인하고 수정할 수 있습니다.</p></div>{!editing && <button type="button" className="my-page-button" onClick={startEdit}>정보 수정</button>}</div>
        {editing ? <form className="my-page-card my-page-form" onSubmit={save} noValidate>
            <label><span>이메일</span><input value={draft.email} disabled /><small>이메일은 로그인 아이디로 사용되어 변경할 수 없습니다.</small></label>
            <label><span>이름</span><input name="name" value={draft.name} onChange={change} onBlur={touch} disabled={saving} required minLength={2} maxLength={50} autoComplete="name" aria-invalid={Boolean(touched.name && errors.name)} />{fieldError('name')}</label>
            <label><span>생년월일</span><input type="date" name="birthDate" value={draft.birthDate} onChange={change} onBlur={touch} disabled={saving} required min="1900-01-01" max={today} autoComplete="bday" aria-invalid={Boolean(touched.birthDate && errors.birthDate)} />{fieldError('birthDate')}</label>
            <label><span>성별</span><select name="gender" value={draft.gender} onChange={change} onBlur={touch} disabled={saving} required aria-invalid={Boolean(touched.gender && errors.gender)}><option value="">선택해주세요</option>{Object.entries(genderLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>{fieldError('gender')}</label>
            <label><span>소속 (선택)</span><input name="affiliation" value={draft.affiliation} onChange={change} onBlur={touch} disabled={saving} maxLength={150} placeholder="학생, 직장인, 연구원 등" autoComplete="organization" />{fieldError('affiliation')}</label>
            {error && <p className="my-page-field-error" role="alert">{error}</p>}
            <div className="my-page-form-actions"><button type="button" className="my-page-button" disabled={saving} onClick={() => setEditing(false)}>취소</button><button type="submit" className="my-page-button primary" disabled={saving}>{saving ? '저장 중…' : '저장'}</button></div>
        </form> : <dl className="my-page-card my-page-info">
            <div><dt>이메일</dt><dd>{display(profile.email)}</dd></div>
            <div><dt>이름</dt><dd>{display(profile.name)}</dd></div>
            <div><dt>생년월일</dt><dd>{display(profile.birthDate)}{age !== '' && <span className="my-page-meta"> · 만 {age}세</span>}</dd></div>
            <div><dt>성별</dt><dd>{display(genderLabels[profile.gender])}</dd></div>
            <div><dt>소속</dt><dd>{display(profile.affiliation)}</dd></div>
            <div><dt>로그인 방식</dt><dd>{display(loginMethods(myPage.hasPassword, myPage.socialAccounts))}</dd></div>
        </dl>}
        {/* <div className="my-page-card my-page-placeholder"><h3>상담 관련 정보 <small>준비 중</small></h3><p>자격·수련 단계, 관심 상담 분야 등 맞춤 연습 추천에 쓰일 정보를 입력할 수 있도록 준비하고 있습니다.</p></div> */}
    </>;
}
